/**
 * Roster Section — coach-side UI for `roster_entries` (the under-13
 * ghost players that don't have User accounts in v1).
 *
 * Sits on the Players page below the regular team_players grid.
 * Renders a list of roster entries with inline add + edit + delete.
 * RLS gates writes to the team's coach; no UI permission check.
 *
 * @see docs/spec/22a-v1-launch-posture.md
 * @see packages/db/migrations/00084_roster_entries.sql
 */
import { useState } from 'react';
import { Button, Card, EmptyState, GlassPill, Skeleton } from './primitives';
import { useRosterEntries } from '../lib/queries/useRosterEntries';
import { addRosterEntry, updateRosterEntry, deleteRosterEntry } from '../lib/rosterEntryCrud';
import { useRateRosterEntry } from '../lib/rosterEntryRatingCrud';
import { rosterEntryAge, type RosterEntry } from '@4cm/core/rosterEntry';
import { CORNER_IDS, type CornerId } from '@4cm/core/rosterEntry/rating';
import { CORNER_LABELS } from '@4cm/core/constants';
import { cornerCssVar } from '../lib/cornerColors';
import './RosterSection.css';

interface RosterSectionProps {
  teamId: string | undefined;
  teamLoading: boolean;
}

export function RosterSection({ teamId, teamLoading }: RosterSectionProps) {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const { rows, state, errorMessage } = useRosterEntries(teamId, teamLoading, {
    refreshKey: refreshNonce,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ratingId, setRatingId] = useState<string | null>(null);

  const refresh = () => setRefreshNonce((n) => n + 1);

  if (!teamId) return null;

  return (
    <Card variant="opaque" className="roster-section">
      <div className="roster-section__head">
        <div>
          <span className="page-eyebrow">🧒 Roster — under-13 squad</span>
          <p className="roster-section__sub">
            Players without their own login yet. Add U7–U12s here — coaches and
            parents see them, but the kids won't have their own account until
            we open it up.
          </p>
        </div>
        <Button
          variant={formOpen ? 'glass' : 'primary'}
          size="touchline"
          onClick={() => {
            if (formOpen) {
              setFormOpen(false);
              setEditingId(null);
            } else {
              setFormOpen(true);
              setEditingId(null);
            }
          }}
        >
          {formOpen ? 'Close' : '+ Add to roster'}
        </Button>
      </div>

      {formOpen && (
        <RosterEntryForm
          teamId={teamId}
          onSaved={() => {
            setFormOpen(false);
            refresh();
          }}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {state === 'loading' && (
        <ul className="roster-list" aria-label="Loading roster">
          {Array.from({ length: 3 }, (_, i) => (
            <li key={i} className="roster-row">
              <Skeleton variant="text" width="40%" height={16} />
              <div style={{ height: 4 }} />
              <Skeleton variant="text" width="20%" height={12} />
            </li>
          ))}
        </ul>
      )}

      {state === 'error' && (
        <p className="roster-section__error">
          Couldn't load the roster.{errorMessage ? ` (${errorMessage})` : null}
        </p>
      )}

      {state === 'empty' && !formOpen && (
        <EmptyState
          glyph="🧒"
          title="No roster entries yet"
          body="Add a U7–U12 player above. They'll appear here with their age and jersey number."
        />
      )}

      {state === 'ok' && rows.length > 0 && (
        <ul className="roster-list">
          {rows.map((entry) =>
            editingId === entry.id ? (
              <li key={entry.id} className="roster-row roster-row--edit">
                <RosterEntryForm
                  teamId={teamId}
                  initial={entry}
                  onSaved={() => {
                    setEditingId(null);
                    refresh();
                  }}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : ratingId === entry.id ? (
              <li key={entry.id} className="roster-row roster-row--edit">
                <RosterRatingForm
                  entry={entry}
                  onSaved={() => setRatingId(null)}
                  onCancel={() => setRatingId(null)}
                />
              </li>
            ) : (
              <RosterRow
                key={entry.id}
                entry={entry}
                onEdit={() => {
                  setEditingId(entry.id);
                  setRatingId(null);
                  setFormOpen(false);
                }}
                onRate={() => {
                  setRatingId(entry.id);
                  setEditingId(null);
                  setFormOpen(false);
                }}
                onDeleted={refresh}
              />
            ),
          )}
        </ul>
      )}
    </Card>
  );
}

// ============================================================
// Single row
// ============================================================

function RosterRow({
  entry,
  onEdit,
  onRate,
  onDeleted,
}: {
  entry: RosterEntry;
  onEdit: () => void;
  onRate: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const age = rosterEntryAge(entry);

  const onDelete = async () => {
    if (!window.confirm(`Remove ${entry.first_name} ${entry.last_name} from the roster?`)) {
      return;
    }
    setBusy(true);
    const result = await deleteRosterEntry(entry.id);
    setBusy(false);
    if (!result.ok) {
      window.alert(`Couldn't remove: ${result.error}`);
      return;
    }
    onDeleted();
  };

  return (
    <li className="roster-row">
      <div className="roster-row__main">
        <div className="roster-row__name">
          {entry.jersey_number != null && (
            <span className="roster-row__num">#{entry.jersey_number}</span>
          )}
          <span>{entry.first_name} {entry.last_name}</span>
          {age != null && <GlassPill mono>U{age + 1}</GlassPill>}
          {entry.position && <span className="roster-row__pos">{entry.position}</span>}
        </div>
        {entry.notes && <div className="roster-row__notes">{entry.notes}</div>}
      </div>
      <div className="roster-row__actions">
        <button
          type="button"
          className="roster-row__btn roster-row__btn--primary"
          onClick={onRate}
          disabled={busy}
          aria-label={`Rate ${entry.first_name} ${entry.last_name}`}
        >
          Rate
        </button>
        <button
          type="button"
          className="roster-row__btn"
          onClick={onEdit}
          disabled={busy}
        >
          Edit
        </button>
        <button
          type="button"
          className="roster-row__btn roster-row__btn--danger"
          onClick={() => void onDelete()}
          disabled={busy}
          aria-label={`Remove ${entry.first_name} ${entry.last_name}`}
        >
          {busy ? '…' : 'Remove'}
        </button>
      </div>
    </li>
  );
}

// ============================================================
// Add / edit form (same shape for both — `initial` flips it to edit mode)
// ============================================================

function RosterEntryForm({
  teamId,
  initial,
  onSaved,
  onCancel,
}: {
  teamId: string;
  initial?: RosterEntry;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState(initial?.first_name ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? '');
  const [dob, setDob] = useState(initial?.dob ?? '');
  const [position, setPosition] = useState(initial?.position ?? '');
  const [jersey, setJersey] = useState<string>(
    initial?.jersey_number != null ? String(initial.jersey_number) : '',
  );
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const isEdit = !!initial;

  const submit = async () => {
    if (saving) return;
    setMessage(null);
    setSaving(true);

    const jerseyNum = jersey === '' ? null : Number(jersey);
    const payload = {
      team_id: teamId,
      first_name: firstName,
      last_name: lastName,
      dob,
      position: position || null,
      jersey_number: Number.isNaN(jerseyNum) ? null : jerseyNum,
      notes: notes || null,
    };

    const result = isEdit
      ? await updateRosterEntry(initial!.id, payload)
      : await addRosterEntry(payload);

    setSaving(false);
    if (!result.ok) {
      setMessage(result.error);
      return;
    }
    onSaved();
  };

  return (
    <div className="roster-form">
      <div className="roster-form__row">
        <label className="roster-form__field">
          <span>First name</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Marco"
            autoFocus
          />
        </label>
        <label className="roster-form__field">
          <span>Last name</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Adams"
          />
        </label>
        <label className="roster-form__field roster-form__field--dob">
          <span>Date of birth</span>
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
          />
        </label>
      </div>
      <div className="roster-form__row">
        <label className="roster-form__field roster-form__field--num">
          <span>#</span>
          <input
            type="number"
            min={0}
            max={99}
            value={jersey}
            onChange={(e) => setJersey(e.target.value)}
            aria-label="Jersey number"
          />
        </label>
        <label className="roster-form__field">
          <span>Position</span>
          <input
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="GK / CB / CM / ST"
            maxLength={40}
          />
        </label>
        <label className="roster-form__field roster-form__field--notes">
          <span>Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            maxLength={1000}
          />
        </label>
      </div>
      <div className="roster-form__actions">
        <Button
          variant="primary"
          size="touchline"
          onClick={() => void submit()}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add player'}
        </Button>
        <Button variant="glass" size="touchline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {message && <p className="roster-form__msg">{message}</p>}
    </div>
  );
}

// ============================================================
// 4-corner quick-rate form
// ============================================================

function RosterRatingForm({
  entry,
  onSaved,
  onCancel,
}: {
  entry: RosterEntry;
  onSaved: () => void;
  onCancel: () => void;
}) {
  // One score per corner — empty string while untouched, integer 0–100
  // once the coach has set it. `null` after submit clears the inputs.
  const [scores, setScores] = useState<Partial<Record<CornerId, string>>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const rate = useRateRosterEntry();

  const setScore = (corner: CornerId, raw: string) => {
    setScores((s) => ({ ...s, [corner]: raw }));
  };

  const submit = async () => {
    if (saving) return;
    setMessage(null);

    const scoresByCorner: Partial<Record<CornerId, number | null>> = {};
    for (const c of CORNER_IDS) {
      const raw = scores[c];
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isInteger(n)) {
        setMessage(`${CORNER_LABELS[c]} score must be a whole number.`);
        return;
      }
      if (n < 0 || n > 100) {
        setMessage(`${CORNER_LABELS[c]} score must be between 0 and 100.`);
        return;
      }
      scoresByCorner[c] = n;
    }

    if (Object.keys(scoresByCorner).length === 0) {
      setMessage('Set at least one corner score before saving.');
      return;
    }

    setSaving(true);
    const result = await rate({
      rateeId: entry.id,
      scoresByCorner,
      context: 'SESSION',
      // No session linkage in this minimal flow — coach is rating
      // ad-hoc (e.g. "after tonight"). A future flow that opens the
      // rater from inside a session screen should pass sessionId.
      sessionId: null,
    });
    setSaving(false);
    if (!result.ok) {
      setMessage(result.error);
      return;
    }
    onSaved();
  };

  return (
    <div className="roster-form roster-form--rating">
      <div className="roster-form__rating-head">
        <strong>{entry.first_name} {entry.last_name}</strong>
        <span className="roster-form__rating-sub">
          0–100 per corner. Leave a corner blank to skip it.
        </span>
      </div>
      <div className="roster-form__rating-grid">
        {CORNER_IDS.map((c) => (
          <label key={c} className="roster-form__rating-field">
            <span
              className="roster-form__rating-label"
              style={{ color: cornerCssVar(c) }}
            >
              {CORNER_LABELS[c]}
            </span>
            <input
              type="number"
              min={0}
              max={100}
              value={scores[c] ?? ''}
              onChange={(e) => setScore(c, e.target.value)}
              aria-label={`${CORNER_LABELS[c]} score`}
              inputMode="numeric"
            />
          </label>
        ))}
      </div>
      <div className="roster-form__actions">
        <Button variant="primary" size="touchline" onClick={() => void submit()}>
          {saving ? 'Saving…' : 'Save ratings'}
        </Button>
        <Button variant="glass" size="touchline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {message && <p className="roster-form__msg">{message}</p>}
    </div>
  );
}
